/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { Icon } from '@zapier/zinnia-icons';
import {
  Accordion,
  Colors,
  Link,
  Text,
  Typography,
} from '@zapier/design-system';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  useQueryAnalytics,
  useQueryInsightsSummaryActivityData,
  useQueryInsightsActivityDailyData,
  useQueryInsightsActiveUsersRetentionData,
} from 'app/developer-v3/hooks';
import { TimePeriodDays } from 'app/developer-v3/hooks/useIntegrationInsights';
import { SummaryIntegrationActivity } from 'app/developer-v3/hooks/useQueryInsightsSummaryActivityData';
import { IntegrationActivityDaily } from 'app/developer-v3/hooks/useQueryInsightsActivityDailyData';
import { Fragment, useState } from 'react';
import BugsAndFeatureTile from './tiles/BugsAndFeatureTile';
import NewUsersTile from './tiles/UserTile';
import Usage from './integrationUsage/Usage';
import { UsageType } from './integrationUsage/types';

import type { SemVer } from 'app/developer-v3/types/version';
import ActiveUsersRetentionTile from './tiles/ActiveUsersRetentionTile';
import ActiveZapsTile from './tiles/ActiveZapsTile';
import DailyLineChartTile from './tiles/DailyLineChartTile';
import MonthlyUsersTile from './tiles/MonthlyUsersTile';
import { useMediaQuery } from 'app/developer-v3/hooks/useMediaQuery';
import ZapActivationRateTile from './tiles/ZapActivationRateTile';
import EmbedInsightsTile from './tiles/EmbedInsightsTile';
import { getAppUrl } from 'app/developer-v3/utils/url/url';

const Styles = {
  root: css`
    height: 100%;
  `,

  page: css`
    display: flex;
    flex-direction: column;
    padding: 25px;

    & h1 {
      padding-top: 0px;
      padding-bottom: 25px;
    }

    & b {
      ${Typography.Body4}
    }

    & p {
      ${Typography.Body1}
    }

    & details {
      & > div {
        /* From the design Tiles have a padding and the accordian body has no margin */
        margin-left: unset !important;
        margin-right: unset !important;
      }
    }
  `,
  grid: css`
    display: grid;
    grid-gap: 1px;
    /* note: grid-template-columns: 1fr 1fr should give us a two column layout we want. However, the google chart component causes uneven column widths. */
    grid-template-columns: repeat(2, minmax(300px, 1fr));

    border-top: 1px solid ${Colors.GrayWarm4};
    border-bottom: 1px solid ${Colors.GrayWarm4};
    background-color: ${Colors.GrayWarm4};

    /* Paint odd rows neutral200 */
    & > :nth-child(4n + 1) {
      background-color: ${Colors.GrayWarm2};
    }
    & > :nth-child(4n + 2) {
      background-color: ${Colors.GrayWarm2};
    }

    /* Paint even rows white */
    & > :nth-child(4n + 3) {
      background-color: ${Colors.PrimeWhite};
    }
    & > :nth-child(4n + 4) {
      background-color: ${Colors.PrimeWhite};
    }

    /* For smaller screens collapse to a single column and alternate bg color */
    @media not all and (min-width: 1200px) {
      grid-template-columns: repeat(1, minmax(300px, 1fr));
      & > :nth-child(2n + 1) {
        background-color: ${Colors.GrayWarm2};
      }
      & > :nth-child(2n + 2) {
        background-color: ${Colors.PrimeWhite};
      }
    }
  `,
  integrationUsageTipContainer: css`
    margin: 0px 20px 10px 20px;
  `,
};

const insightsPageContainer = css`
  display: flex;
  flex-direction: column;
  gap: 15px 0;
`;

type Props = {
  appId: string;
  isPublished: boolean;
  hasActions?: boolean;
  hasTriggers?: boolean;
  redirectToHome: () => void;
  serviceId: number;
  versions: SemVer[];
};

const AccordionHeader = ({ children }) => (
  <Text fontWeight={600} tag="h2" type="SectionHeader">
    {children}
  </Text>
);

/**
 * Extract new users count for a given timeperiod
 * @param data: SummaryIntegrationActivity
 * @param timePeriod: TimePeriodDays, default is set to 30 days
 * @returns number | undefined
 */
const getNewUsersCount = (
  data: SummaryIntegrationActivity[],
  timePeriod: TimePeriodDays = TimePeriodDays.Last_Thirty_days
): number | undefined => {
  if (data === null || data.length === 0) {
    return undefined;
  }

  // extract data, for now the 0-index contains the summary data
  const [summaryData] = data || [];
  const { new_user_last_30days, new_user_last_7days, new_user_last_90days } =
    summaryData || {};

  switch (timePeriod) {
    case TimePeriodDays.Last_Seven_days:
      return new_user_last_7days;
    case TimePeriodDays.Last_Thirty_days:
      return new_user_last_30days;
    case TimePeriodDays.Last_Ninety_days:
      return new_user_last_90days;
    default:
      return undefined;
  }
};

const getActiveZapsCount = (
  data: SummaryIntegrationActivity[],
  timePeriod: TimePeriodDays = TimePeriodDays.Last_Thirty_days
): number | undefined => {
  if (data === null || data.length === 0) {
    return undefined;
  }

  // extract data, for now the 0-index contains the summary data
  const [summaryData] = data || [];
  const {
    active_zaps_last_7days,
    active_zaps_last_30days,
    active_zaps_last_90days,
  } = summaryData || {};

  switch (timePeriod) {
    case TimePeriodDays.Last_Seven_days:
      return active_zaps_last_7days;
    case TimePeriodDays.Last_Thirty_days:
      return active_zaps_last_30days;
    case TimePeriodDays.Last_Ninety_days:
      return active_zaps_last_90days;
    default:
      return undefined;
  }
};

const getDailyActivity = (
  data: IntegrationActivityDaily[],
  timePeriod: TimePeriodDays = TimePeriodDays.Last_Thirty_days
): IntegrationActivityDaily[] => {
  if (data === null || data.length === 0) {
    return undefined;
  }
  const dailyData = data || [];
  return dailyData.slice(0, timePeriod);
};

const InsightsPage = ({
  appId,
  hasActions,
  hasTriggers,
  isPublished,
  versions,
}: Props) => {
  const [newUsersCountTimePeriod, setNewUsersCountTimePeriod] = useState<
    TimePeriodDays
  >(TimePeriodDays.Last_Thirty_days);
  const [activeZapsTimePeriod, setActiveZapsTimePeriod] = useState<
    TimePeriodDays
  >(TimePeriodDays.Last_Thirty_days);
  const [dailyActiveUsersTimePeriod, setDailyActiveUsersTimePeriod] = useState<
    TimePeriodDays
  >(TimePeriodDays.Last_Thirty_days);
  const [dailyActiveZapsTimePeriod, setDailyActiveZapsTimePeriod] = useState<
    TimePeriodDays
  >(TimePeriodDays.Last_Thirty_days);

  const {
    data: summaryActivityData,
    isLoading: summaryActivityIsLoading,
    isError: summaryActivityIsError,
  } = useQueryInsightsSummaryActivityData(appId);
  const {
    data: activityDailyData,
    isLoading: activityDailyIsLoading,
    isError: activityDailyIsError,
  } = useQueryInsightsActivityDailyData(appId);

  const {
    data: activationRetentionData,
    isLoading: activationRetentionIsLoading,
    isError: activationRetentionIsError,
  } = useQueryInsightsActiveUsersRetentionData(appId);

  const newUsersCount = getNewUsersCount(
    summaryActivityData,
    newUsersCountTimePeriod
  );
  const activeZapsCount = getActiveZapsCount(
    summaryActivityData,
    activeZapsTimePeriod
  );
  const dailyActiveUsers = getDailyActivity(
    activityDailyData,
    dailyActiveUsersTimePeriod
  );
  const dailyActiveZaps = getDailyActivity(
    activityDailyData,
    dailyActiveZapsTimePeriod
  );

  const latestVersion = versions?.[versions.length - 1];

  // Integration usage trigger analytics
  const [triggersAnalyticsVersion, setTriggersAnalyticsVersion] = useState(
    latestVersion
  );

  // Integration usage actions analytics
  const [actionsAnalyticsVersion, setActionsAnalyticsVersion] = useState(
    latestVersion
  );

  const {
    isLoading: isLoadingUsageActionAnalytics,
    data: dataUsageActionAnalytics,
  } = useQueryAnalytics({
    integrationId: Number(appId),
    version: actionsAnalyticsVersion,
  });
  const createsAnalytics = dataUsageActionAnalytics?.creates ?? [];
  const searchesAnalytics = dataUsageActionAnalytics?.searches ?? [];
  const actionAnalyticsTimestamp = dataUsageActionAnalytics?.timestamp;

  const {
    isLoading: isLoadingUsageTriggerAnalytics,
    data: dataUsageTriggerAnalytics,
  } = useQueryAnalytics({
    integrationId: Number(appId),
    version: triggersAnalyticsVersion,
  });
  const triggersAnalytics = dataUsageTriggerAnalytics?.triggers ?? [];
  const triggerAnalyticsTimestamp = dataUsageTriggerAnalytics?.timestamp;

  const isViewportWide = useMediaQuery('(min-width: 1200px)');

  // Not standard Zinnia v5 colors; these are part of our colorblind-friendly
  // palette
  const lineColor1 = '#22357b';
  const lineColor2 = '#40a6ff';
  const backgroundColor1 = Colors.GrayWarm2;
  const backgroundColor2 = Colors.PrimeWhite;

  // Wide viewports use a 2 column layout, so the indices look like this:
  //
  // +-----+-----+
  // |  0  |  1  |
  // +-----+-----+
  // |  2  |  3  |
  // +-----+-----+
  // |  4  |  5  |
  // +-----+-----+
  // |  6  |  7  |
  // +-----+-----+
  //
  function getLineColorForIndex(index: number) {
    if (isViewportWide) {
      return index % 4 === 0 || index % 4 === 1 ? lineColor1 : lineColor2;
    }
    return index % 2 === 0 ? lineColor1 : lineColor2;
  }
  function getBackgroundColorForIndex(index: number) {
    if (isViewportWide) {
      return index % 4 === 0 || index % 4 === 1
        ? backgroundColor1
        : backgroundColor2;
    }
    return index % 2 === 0 ? backgroundColor1 : backgroundColor2;
  }

  // You might be wondering why the tiles are in an array. Well, we need to pass
  // a different color to the Google Chart line chart depending on the
  // background color. The background color is dependent on the viewport size (1
  // column vs 2 column) and the number of elements shown. The number of
  // elements shown is conditional based on feature flags. This means we can't
  // simply hard code the color value for the line chart. We want to use CSS as
  // the source of truth on these colors, but Google Charts won't let us pass
  // CSS custom property names (e.g. `var(--our-color)`) to the line chart, even
  // though that's actually valid in SVG. It literally just crashes. Ew. You
  // might consider moving _all_ the "striped" color logic into JS so we don't
  // have to duplicate it between CSS and JS, but I'm not sure it's worth
  // bothering right now, or maybe ever. Good luck, have fun.
  //
  // Sage Fennel (#team-partner-sharing)
  // 2022-12-15
  const tileRenderers: ((index: number) => JSX.Element)[] = [
    () => (
      <NewUsersTile
        appId={appId}
        dataTestId="NewUsersTile"
        isError={summaryActivityIsError}
        isLoading={summaryActivityIsLoading}
        key="NewUsersTile"
        newUsersCount={newUsersCount}
        onSelect={setNewUsersCountTimePeriod}
      />
    ),
    () => (
      <BugsAndFeatureTile
        appId={appId}
        dataTestId="BugsAndFeatureTile"
        key="BugsAndFeatureTile"
      />
    ),
    i => (
      <DailyLineChartTile
        data={dailyActiveUsers?.map(({ date, users_active_daily }) => ({
          dateString: date,
          value: users_active_daily,
        }))}
        dataTestId="DailyActiveUsersTile"
        headerContent="Daily active users"
        icon="personOne"
        isError={activityDailyIsError}
        isLoading={activityDailyIsLoading}
        key="DailyActiveUsersTile"
        lineColor={getLineColorForIndex(i)}
        onSelect={setDailyActiveUsersTimePeriod}
        tooltipContent={
          <Text color="PrimeWhite" tag="p" type="Body1">
            Number of users who had a Zap activated in a day.
          </Text>
        }
        xAxisLabel="Zap Used Date"
        yAxisLabel="Count Activated Users"
      />
    ),
    i => (
      <MonthlyUsersTile
        appId={appId}
        columnColor={getLineColorForIndex(i)}
        dataTestId="MonthlyUsersTile"
        key="MonthlyUsersTile"
      />
    ),
    () => (
      <ActiveZapsTile
        activeZapsCount={activeZapsCount}
        dataTestId="ActiveZapsTile"
        isError={summaryActivityIsError}
        isLoading={summaryActivityIsLoading}
        key="ActiveZapsTile"
        onSelect={setActiveZapsTimePeriod}
      />
    ),
    i => (
      <DailyLineChartTile
        data={dailyActiveZaps?.map(({ date, zaps_active_daily }) => ({
          dateString: date,
          value: zaps_active_daily,
        }))}
        dataTestId="DailyActiveZapsTile"
        headerContent="Daily active Zap workflows"
        icon="miscBoltAlt"
        isError={activityDailyIsError}
        isLoading={activityDailyIsLoading}
        key="DailyActiveZapsTile"
        lineColor={getLineColorForIndex(i)}
        onSelect={setDailyActiveZapsTimePeriod}
        tooltipContent={
          <Text color="PrimeWhite" tag="p" type="Body1">
            Number of active Zap workflows in a day. Active Zap workflows =
            number of Zap workflows using this integration.
          </Text>
        }
        xAxisLabel="Zap Used Date"
        yAxisLabel="Count Unique Zap Workflows"
      />
    ),
  ];
  // Because grid layouts don't support borders _between_ items, we simulate
  // this by setting the border color as the background of the entire grid, and
  // then specifying a 1px gap between each item, so it looks like there's
  // borders between them. The problem is, this means we have a big nasty gray
  // rectangle in 2-column layout if the number of items is odd :( So let's add
  // a little "load bearing" div that gets styled by CSS to hide our CSS hack.
  //
  // Sage Fennel (#team-partner-sharing)
  // 2022-12-15
  if (isViewportWide && tileRenderers.length % 2 === 1) {
    tileRenderers.push(() => (
      <div data-filler-tile={true} key={'FillerTile'} />
    ));
  }
  const tiles = tileRenderers.filter(fn => fn).map((fn, i) => fn(i));

  return (
    <div css={Styles.page}>
      <Text
        fontWeight={600}
        margin="0px 0px 25px 0px"
        tag="h1"
        type="PageHeader"
      >
        Insights
      </Text>
      <div css={insightsPageContainer}>
        {isPublished && (
          <div>
            <Accordion
              isInitiallyOpen={true}
              title={<AccordionHeader>Integration growth</AccordionHeader>}
              toggleSide="right"
            >
              <div css={Styles.grid}>{tiles}</div>
              <div
                style={{
                  background: getBackgroundColorForIndex(tiles.length),
                }}
              >
                <ActiveUsersRetentionTile
                  data={activationRetentionData}
                  dataTestId="ActiveUsersRetentionTile"
                  headerContent="Active users retention"
                  icon={<Icon name="personOne" size="24px" />}
                  isError={activationRetentionIsError}
                  isLoading={activationRetentionIsLoading}
                  key="ActiveUsersRetentionTile"
                  tooltipContent={
                    <Text color="PrimeWhite" tag="p" type="Body1">
                      The percentage of active users that are retained each
                      month. Retention = users who have at least one successful
                      task executed in a given month.
                    </Text>
                  }
                />
              </div>
            </Accordion>
          </div>
        )}
        {isPublished && (
          <div>
            <Accordion
              isInitiallyOpen={true}
              title={
                <AccordionHeader>
                  Integration activation success rate
                </AccordionHeader>
              }
              toggleSide="right"
            >
              <ZapActivationRateTile
                appId={appId}
                dataTestId="ZapActivationRateByTriggerTile"
                title="Zap activation rate by trigger"
                tooltipContent={
                  <Fragment>
                    The higher the rate, the better the trigger is performing.{' '}
                    <br />
                    <br />
                    Zap Activation Rate = For a given trigger, look at the
                    percentage of Zap workflows that use that trigger and the
                    rate of the ones that activated* within 24 hours of
                    creation. <br />
                    <br />
                    (*Activated means it successfully ran at least one task).
                  </Fragment>
                }
                type="triggers"
              />
              <ZapActivationRateTile
                appId={appId}
                dataTestId="ZapActivationRateByActionTile"
                tipContent={
                  <Text type="Body1">
                    To improve the Zap Activation Rate of a trigger or action,
                    see the{' '}
                    <Link href={`${getAppUrl(appId)}/issues`}>
                      bugs and feature requests
                    </Link>{' '}
                    related to that trigger or action.
                  </Text>
                }
                tipContentStyle={{ marginTop: 30 }}
                title="Zap activation rate by action"
                tooltipContent={
                  <Fragment>
                    The higher the rate, the better the action is performing.{' '}
                    <br />
                    <br />
                    Zap Activation Rate = For a given action, look at the
                    percentage of Zap workflows that use that action and the
                    rate of the ones that activated* within 24 hours of
                    creation. <br />
                    <br />
                    (*Activated means it successfully ran at least one task).
                  </Fragment>
                }
                type="actions"
              />
            </Accordion>
          </div>
        )}
        {isPublished && (
          <div>
            <Accordion
              isInitiallyOpen={true}
              title={<AccordionHeader>Embed insights</AccordionHeader>}
              toggleSide="right"
            >
              <EmbedInsightsTile appId={appId} dataTestId="EmbedInsightsTile" />
            </Accordion>
          </div>
        )}
        <div>
          <Accordion
            isInitiallyOpen={true}
            title={<AccordionHeader>Integration usage</AccordionHeader>}
            toggleSide="right"
          >
            <Fragment>
              <Usage
                analytics={hasTriggers ? triggersAnalytics : []}
                ariaLabel="Trigger Analytics"
                dataTestId="UsageByTriggerTile"
                isLoading={isLoadingUsageTriggerAnalytics}
                onChangeVersion={setTriggersAnalyticsVersion}
                timestamp={triggerAnalyticsTimestamp}
                title="Usage by trigger"
                type={UsageType.trigger}
                version={triggersAnalyticsVersion}
                versions={versions}
              />
              <Usage
                analytics={
                  hasActions ? [...createsAnalytics, ...searchesAnalytics] : []
                }
                ariaLabel="Action Analytics"
                dataTestId="UsageByActionTile"
                isLoading={isLoadingUsageActionAnalytics}
                onChangeVersion={setActionsAnalyticsVersion}
                timestamp={actionAnalyticsTimestamp}
                title="Usage by action"
                type={UsageType.action}
                version={actionsAnalyticsVersion}
                versions={versions}
              />
              <div css={Styles.integrationUsageTipContainer}>
                <Text type="Body3">{'TIP: '}</Text>
                <Text type="Body1">
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/zap-templates`}
                    target="_blank"
                  >
                    Create Zap templates
                  </Link>
                  {
                    ' for your most popular triggers and actions that users can discover and turn on right away.'
                  }
                </Text>
              </div>
            </Fragment>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default InsightsPage;
